import { ActivityList } from "@entities/activity/ui/ActivityList/ActivityList";
import { useGetAddRightsByBriefInfluencerId } from "@entities/addRights/lib/hooks/useGetAddRightsByBriefInfluencerId";
import { useStartAddRight } from "@entities/addRights/lib/hooks/useStartAddRight";
import {
  BriefDeliverableStatusEnum,
  BriefDeliverableTaskTypeEnum,
  UploaderType,
} from "@entities/brief/lib/constants";
import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useGetBriefCampaignInfluner } from "@entities/campaigns/lib/hooks/useGetBriefCampaignInfluner";
// import AddCampaignBrief from "@features/AddCampaignBrief";
// import AddCampaignDraft from "@features/AddCampaignDraft/AddCampaignDraft";
// import AddCampaignLive from "@features/AddCampaignLive";
// import AddCampaignScript from "@features/AddCampaignScript/AddCampaignScript";
import AddCampaignBrief from "@features/AddCampaignBrief";
import AddCampaignDraft from "@features/AddCampaignDraft/AddCampaignDraft";
import AddCampaignLive from "@features/AddCampaignLive";
import AddCampaignScript from "@features/AddCampaignScript/AddCampaignScript";
import { ToastService } from "@shared/lib/services";
import { BreifInfluencerV2MainStatusEnum } from "@shared/lib/types/brief";
import {
  ICampaignDeliverable,
  ICampaignDeliverableTask,
} from "@shared/lib/types/briefCampains";
import { Divider, Popover, Tabs } from "@shared/ui";
import { CreatorInfo } from "@shared/ui/CreatorInfo";
import { Close } from "@shared/ui/icons";
import {
  Collapse,
  Drawer,
  Empty,
  Flex,
  Skeleton
} from "antd";
import dayjs from "dayjs";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  History
} from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { AdRightsCard } from "./ui/AdRightsCard";
import { CreatorContactSection } from "./ui/CreatorContactSection";
import { EmptyState } from "./ui/EmptyState";
import { ExecutionCommercialsSummary } from "./ui/ExecutionCommercialsSummary";
import {
  getStatusLabel,
  getStatusTagClassKey,
  getTaskIcon,
  getTaskIconColorClassKey,
  getTaskStatusColorClassKey,
  getTaskStatusLabel,
} from "./utils";

interface CampaignBriefInfluencerDrawerProps {
  open: boolean;
  onClose: () => void;
  campaignInfluencerId?: string;
  briefInfluencerId?: string;
  taskId?: string;
  deliverableConfigRefId?: string;
  adRightsTrackerId?: string;
}

export default function CampaignBriefInfluencerDrawer({
  open,
  onClose,
  campaignInfluencerId,
  briefInfluencerId,
  deliverableConfigRefId,
  taskId,
  adRightsTrackerId,
}: CampaignBriefInfluencerDrawerProps) {
  const { briefInfluencer, campaignInfluencer, finalBrandCost, finalInfluencerCost, isLoading, isError, refetch } =
    useGetBriefCampaignInfluner({
      campaignInfluencerId,
      briefInfluencerId,
      enabled: open,
    });

  const { addRights, isLoading: isLoadingAddRights, refetchAdRights} =
    useGetAddRightsByBriefInfluencerId(briefInfluencer?._id || "");

  const { startAdRight, isStarting, isStarted, variables } = useStartAddRight(
    () => {
      ToastService.success("Rights started successfully");
      refetch();
    }
  );

  const data = addRights?.data;

  const [expandedAddRightKey, setExpandedAddRightKey] = useState<string | null>(
    null
  );
  const [editingStartDateAddRightId, setEditingStartDateAddRightId] = useState<
    string | null
  >(null);
  const [tempStartDate, setTempStartDate] = useState<string | null>(null);

  useEffect(() => {
    if (open && adRightsTrackerId && !isLoadingAddRights) {
      setExpandedAddRightKey(adRightsTrackerId);

      const timer = setTimeout(() => {
        const element = document.getElementById(`ad-right-${adRightsTrackerId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open, adRightsTrackerId, isLoadingAddRights]);

  const { config } = useGetBriefDeliverableConfig(
    briefInfluencer?.briefId || ""
  );

  const [activeTabKey, setActiveTabKey] = useState<string | null>(
    deliverableConfigRefId || null
  );
  const [expandedTimelineStep, setExpandedTimelineStep] = useState<
    string | null
  >(
    deliverableConfigRefId && taskId
      ? `${deliverableConfigRefId}-${taskId}`
      : null
  );

  // Determine brief status - completed if deliverables are attached or confirmed

  const deliverables = (() => {
    if (!config) return [];
    return config
      .filter((item) => item.isSaved)
      .filter(
        (item) =>
          campaignInfluencer?.deliverables?.findIndex(
            (d) => d._id === item.deliverableConfigRefId
          ) !== -1
      )
      .map((item) => ({
        label: item.name,
        key: item.deliverableConfigRefId,
        type: item.deliverableType,
        tasks: item.tasks || [],
      }));
  })();

  const getDeliverableData = (
    deliverableConfigRefId: string
  ): ICampaignDeliverable | undefined => {
    return campaignInfluencer?.deliverables?.find(
      (d) => d._id === deliverableConfigRefId
    );
  };

  const getTaskData = (
    deliverable: ICampaignDeliverable | undefined,
    taskId: string
  ): ICampaignDeliverableTask | undefined => {
    return deliverable?.tasks?.find((t) => t._id === taskId);
  };

  useEffect(() => {
    if (open) {
      if (deliverableConfigRefId) {
        setActiveTabKey(deliverableConfigRefId);
      }
      if (deliverableConfigRefId && taskId) {
        setExpandedTimelineStep(`${deliverableConfigRefId}-${taskId}`);
      }
    } else {
      setActiveTabKey(null);
      setExpandedTimelineStep(null);
    }
  }, [deliverableConfigRefId, taskId, open]);

  const mainStatus = briefInfluencer?.mainStatus;
  const statusLabel = mainStatus ? getStatusLabel(mainStatus) : "";
  const statusTagClass = mainStatus
    ? styles[getStatusTagClassKey(mainStatus)]
    : "";
  const campaignName = briefInfluencer?.brief?.name;

  const isCancelledOrWithdrawn =
    mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled ||
    mainStatus === BreifInfluencerV2MainStatusEnum.Withdrawn;

  useEffect(() => {
    if (isCancelledOrWithdrawn) {
      setExpandedTimelineStep(null);
    }
  }, [isCancelledOrWithdrawn]);

  const tabItems = deliverables.map((deliverable) => {
    const deliverableData = getDeliverableData(deliverable.key);

    return {
      key: deliverable.key,
      label: deliverable.label,
      children: (
        <div className={styles.tabContent}>
          {/* Execution Timeline */}
          <div className={styles.executionTimeline}>
            <div className={styles.timelineHeader}>
              <h4 className={styles.timelineHeaderTitle}>
                <Clock size={14} className={styles.timelineHeaderIcon} />{" "}
                Execution Timeline
              </h4>
              {campaignInfluencer?.purchaseItems?.find((val) => val.campaignDeliverableId === deliverable?.key)?.isCancelled ? <span className={styles.cancelledTag}>
                Cancelled
              </span> : null}
            </div>
            <div className={styles.timelineContent}>
              {/* Task Segments */}
              {deliverable.tasks.map((taskConfig) => {
                const taskData = getTaskData(
                  deliverableData,
                  taskConfig.taskId
                );
                const taskStatus = taskData?.status;
                const approvalPendingOn = taskData?.approvalPendingOn;
                const taskTimeline = taskData?.timeline;
                // Check if it's "Needs Approval"
                const isNeedsApproval =
                  taskStatus &&
                  taskStatus !== BriefDeliverableStatusEnum.Pending &&
                  taskStatus !== BriefDeliverableStatusEnum.Completed &&
                  taskStatus !== BriefDeliverableStatusEnum.Approved &&
                  approvalPendingOn !== UploaderType.Brand;
                // Check if it's "In Review" status

                const isInReview = approvalPendingOn === UploaderType.Brand;

                const isTaskActive =
                  taskStatus === BriefDeliverableStatusEnum.Completed ||
                  taskStatus === BriefDeliverableStatusEnum.Approved ||
                  taskStatus === BriefDeliverableStatusEnum.PendingApproval ||
                  taskStatus === BriefDeliverableStatusEnum.EditsRequested ||
                  isInReview ||
                  isNeedsApproval;

                const TaskIcon = getTaskIcon(taskConfig.type);
                const iconColorClassKey = getTaskIconColorClassKey(
                  taskStatus,
                  approvalPendingOn
                );
                const iconColorClass = iconColorClassKey
                  ? styles[iconColorClassKey]
                  : "";

                return (
                  <div
                    key={taskConfig.taskId}
                    className={styles.timelineSegment}
                  >
                    <div
                      className={`${styles.timelineSegmentHeader} ${
                        expandedTimelineStep ===
                        `${deliverable.key}-${taskConfig.taskId}`
                          ? styles.timelineSegmentHeaderActive
                          : ""
                      } ${isCancelledOrWithdrawn ? styles.timelineSegmentHeaderDisabled : ""}`}
                      onClick={() => {
                        if (isCancelledOrWithdrawn) return;
                        setExpandedTimelineStep(
                          expandedTimelineStep ===
                            `${deliverable.key}-${taskConfig.taskId}`
                            ? null
                            : `${deliverable.key}-${taskConfig.taskId}`
                        );
                      }}
                    >
                      <div className={styles.timelineSegmentLeft}>
                        <div
                          className={`${styles.timelineSegmentIcon} ${iconColorClass}`}
                        >
                          <TaskIcon size={14} />
                        </div>
                        <span
                          className={`${styles.timelineSegmentTitle} ${
                            isTaskActive
                              ? styles.timelineSegmentTitleActive
                              : ""
                          }`}
                        >
                          {taskConfig.name}
                        </span>
                      </div>
                      <div className={styles.timelineSegmentRight}>
                        <span
                          className={`${styles.timelineStatusTag} ${styles[getTaskStatusColorClassKey(taskStatus, approvalPendingOn)]} ${
                            isCancelledOrWithdrawn
                              ? styles.cancelledWithdrawnStrikethrough
                              : ""
                          }`}
                        >
                          {isCancelledOrWithdrawn
                            ? "Cancelled"
                            : getTaskStatusLabel(taskStatus, approvalPendingOn)}
                        </span>
                        {taskTimeline && taskTimeline.length > 0 && (
                          <Popover
                            trigger="click"
                            placement="topRight"
                            overlayClassName={styles.taskHistoryPopover}
                            content={
                              <div className={styles.taskHistoryPopoverInner}>
                                <div className={styles.taskHistoryPopoverTitle}>
                                  {taskConfig.name} History
                                </div>
                                <div className={styles.taskHistoryPopoverList}>
                                  {taskTimeline
                                    .filter(
                                      (entry) =>
                                        entry.status !==
                                        BriefDeliverableStatusEnum.Pending
                                    )
                                    .map((entry, idx) => {
                                      const user =
                                        entry.createdBy?.firstName != null &&
                                        entry.createdBy?.lastName != null
                                          ? `${entry.createdBy.firstName} ${entry.createdBy.lastName}`.trim()
                                          : "—";
                                      const dateStr =
                                        entry.createdAt &&
                                        dayjs(entry.createdAt).isValid()
                                          ? dayjs(entry.createdAt).format(
                                              "DD MMM YYYY • HH:mm"
                                            )
                                          : "—";
                                      const action = getTaskStatusLabel(
                                        entry.status as BriefDeliverableStatusEnum,
                                        entry.uploaderApproverType
                                      );
                                      return (
                                        <div
                                          key={idx}
                                          className={
                                            styles.taskHistoryPopoverItem
                                          }
                                        >
                                          <div
                                            className={
                                              styles.taskHistoryPopoverBar
                                            }
                                          />
                                          <div
                                            className={
                                              styles.taskHistoryPopoverItemContent
                                            }
                                          >
                                            <div
                                              className={
                                                styles.taskHistoryPopoverAction
                                              }
                                            >
                                              {action}
                                            </div>
                                            <div
                                              className={
                                                styles.taskHistoryPopoverMeta
                                              }
                                            >
                                              {dateStr} • {user}{" "}
                                              {entry.status ===
                                              BriefDeliverableStatusEnum.Approved
                                                ? ` (${entry.uploaderApproverType})`
                                                : ""}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            }
                          >
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              className={styles.taskHistoryIconBtn}
                              aria-label="Task history"
                            >
                              <History size={14} />
                            </button>
                          </Popover>
                        )}
                        {!isCancelledOrWithdrawn &&
                          (expandedTimelineStep ===
                          `${deliverable.key}-${taskConfig.taskId}` ? (
                            <ChevronUp
                              size={14}
                              className={styles.timelineChevron}
                            />
                          ) : (
                            <ChevronDown
                              size={14}
                              className={styles.timelineChevron}
                            />
                          ))}
                      </div>
                    </div>

                    {!isCancelledOrWithdrawn &&
                      expandedTimelineStep ===
                        `${deliverable.key}-${taskConfig.taskId}` && (
                        <div className={styles.timelineSegmentContent}>
                          {taskConfig.type ===
                          BriefDeliverableTaskTypeEnum.Brief ? (
                            <AddCampaignBrief
                              briefId={briefInfluencer?.briefId || ""}
                              taskId={taskConfig.taskId}
                              campaignInfluencerId={
                                campaignInfluencer?._id || ""
                              }
                              deliverableConfigRefId={deliverable.key}
                              task={taskData}
                              refetchInfluencer={refetch}
                              isLastApproval={
                                taskData?.approvalPendingOn ===
                                taskConfig?.approvalType?.at(-1)
                              }
                            />
                          ) : taskConfig.type ===
                            BriefDeliverableTaskTypeEnum.Script ? (
                            <AddCampaignScript
                              taskId={taskConfig.taskId}
                              campaignInfluencerId={
                                campaignInfluencer?._id || ""
                              }
                              deliverableConfigRefId={deliverable.key}
                              taskForm={taskData?.form}
                              refetchInfluencer={refetch}
                              taskStatus={taskStatus!}
                              scriptingId={taskData?.scriptingId || ""}
                              briefId={briefInfluencer?.briefId || ""}
                            />
                          ) : taskConfig.type ===
                            BriefDeliverableTaskTypeEnum.Draft ? (
                            <AddCampaignDraft
                              taskId={taskConfig.taskId}
                              campaignInfluencerId={
                                campaignInfluencer?._id || ""
                              }
                              deliverableConfigRefId={deliverable.key}
                              refetchInfluencer={refetch}
                              taskForm={taskData?.form}
                              taskStatus={taskStatus!}
                              draftId={taskData?.form?.draftingId || ""}
                              briefId={briefInfluencer?.briefId || ""}
                            />
                          ) : taskConfig.type ===
                            BriefDeliverableTaskTypeEnum.Live ? (
                            <AddCampaignLive
                              taskId={taskConfig.taskId}
                              campaignInfluencerId={
                                campaignInfluencer?._id || ""
                              }
                              deliverableConfigRefId={deliverable.key}
                              refetchInfluencer={refetch}
                              taskForm={taskData?.form}
                              briefId={briefInfluencer?.briefId || ""}
                            />
                          ) : !taskData ? (
                            <div className={styles.emptyTaskState}>
                              <p className={styles.emptyTaskText}>
                                Task not started
                              </p>
                            </div>
                          ) : (
                            <div className={styles.taskContent}>
                              <p className={styles.taskContentText}>
                                {/* Task: {taskData.taskName} - Status:{" "}
                              {taskData.status} */}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
          <Divider style={{ marginBottom: 20 }} />
          <AdRightsCard
            addRights={data || []}
            deliverables={deliverables}
            expandedAddRightKey={expandedAddRightKey}
            onExpandedAddRightKeyChange={setExpandedAddRightKey}
            editingStartDateAddRightId={editingStartDateAddRightId}
            onEditingStartDateAddRightIdChange={setEditingStartDateAddRightId}
            tempStartDate={tempStartDate}
            onTempStartDateChange={setTempStartDate}
            onStartAdRight={startAdRight}
            isStarting={isStarting}
            refetch={() => {
              refetch();
              refetchAdRights();
            }}
          />
          <Divider style={{ marginBottom: 20 }} />
          <Collapse
            className={styles.collapse}
            items={[
              {
                key: "activityLog",
                label: (
                  <Flex gap={5} align="center">
                    <Clock size={16} color="#635bff" />
                    Activity Log
                  </Flex>
                ),
                children: (
                  // <NoPermissionPage
                  //   moduleName="Activity Log"
                  //   permissionKey="view-activity-logs-brand-org"
                  // >
                  <ActivityList
                    briefInfluencerIds={
                      briefInfluencer ? [briefInfluencer?._id] : []
                    }
                    datePosition="start"
                  />
                  // </NoPermissionPage>
                ),
              },
            ]}
            expandIconPosition="end"
          />
        </div>
      ),
    };
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={650}
      closeIcon={false}
      closable={false}
      title={
        !briefInfluencer ? (
          <div className={styles.drawerHeader}>
            <div className={styles.headerLeft}>
              <Flex align="center" gap={12}>
                <Skeleton.Avatar active size={35} shape="circle" />
                <Flex vertical align="center" gap={1}>
                  <Skeleton.Input active style={{ width: 150, height: 16 }} />
                  <Skeleton.Input active style={{ width: 100, height: 12 }} />
                </Flex>
              </Flex>
            </div>
          </div>
        ) : (
          <div className={styles.drawerHeader}>
            <div className={styles.headerLeft}>
              <CreatorInfo
                name={briefInfluencer.influencerName}
                username={
                  briefInfluencer.socialMedia?.username ||
                  briefInfluencer.influencer?.firstName ||
                  ""
                }
                link={briefInfluencer.socialMedia?.link}
                platform={briefInfluencer.socialMedia?.platform}
                profilePicUrl={briefInfluencer.socialMedia?.profilePicUrl}
                campaignName={campaignName}
                statusTag={
                  <span className={`${styles.statusTag} ${statusTagClass}`}>
                    {statusLabel}
                  </span>
                }
              />
            </div>
            <button onClick={onClose} className={styles.closeButton}>
              <Close size={20} />
            </button>
          </div>
        )
      }
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        header: {
          padding: 0,
          borderBottom: "1px solid #e3e8ee",
        },
      }}
      className={styles.drawer}
    >
      <div className={styles.drawerBody}>
        {!briefInfluencer ? (
          <EmptyState />
        ) : (
          <>
            {isCancelledOrWithdrawn && (
              <div
                className={`${styles.cancelledWithdrawnAlert} ${
                  mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled
                    ? styles.cancelledWithdrawnAlertCancelled
                    : styles.cancelledWithdrawnAlertWithdrawn
                }`}
              >
                <AlertTriangle
                  size={16}
                  className={
                    mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled
                      ? styles.cancelledWithdrawnAlertIconCancelled
                      : styles.cancelledWithdrawnAlertIconWithdrawn
                  }
                />
                <div>
                  <h4
                    className={
                      mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled
                        ? styles.cancelledWithdrawnAlertTitleCancelled
                        : styles.cancelledWithdrawnAlertTitleWithdrawn
                    }
                  >
                    Creator{" "}
                    {mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled
                      ? "cancelled"
                      : "withdrawn"}{" "}
                    from campaign
                  </h4>
                  <p
                    className={
                      mainStatus === BreifInfluencerV2MainStatusEnum.Cancelled
                        ? styles.cancelledWithdrawnAlertDescCancelled
                        : styles.cancelledWithdrawnAlertDescWithdrawn
                    }
                  >
                    This creator is no longer active in execution for this
                    campaign.
                  </p>
                </div>
              </div>
            )}
            <CreatorContactSection
              contact={briefInfluencer?.primaryContact}
              isContactAdded={briefInfluencer?.isPrimaryContactAdded || false}
            />

            <ExecutionCommercialsSummary
              briefInfluencer={briefInfluencer}
              campaignInfluencer={campaignInfluencer}
              finalBrandCost={finalBrandCost}
              finalInfluencerCost={finalInfluencerCost}
              onPocUpdated={refetch}
              defaultExpanded={
                mainStatus !== BreifInfluencerV2MainStatusEnum.Campaign
              }
            />

            {/* Ad Rights & Usage - flat list: one row per add right (or one NO RIGHTS row per deliverable) */}

            {mainStatus ===
            BreifInfluencerV2MainStatusEnum.Brief ? null : deliverables.length >
              0 ? (
              <>
                <Tabs
                  activeKey={activeTabKey || deliverables[0]?.key}
                  onChange={setActiveTabKey}
                  items={tabItems}
                  className={styles.deliverablesTabs}
                />
              </>
            ) : (
              <Empty
                description="No deliverables configured"
                style={{ padding: 40 }}
              />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
